/**
 * DiceUtil - Utilitaire pour les calculs de dés du système D6
 * Équivalent de DiceUtil.java dans le backend et dice_util.dart en Flutter
 */

import {getGlobalSeededRandom} from './SeededRandom';

const DICE_PATTERN = /^([-+])?([0-9]+)([dD]([-+][0-9]+)?)?$/;

/**
 * Lance un dé à 6 faces (1-6) avec seed
 */
export function rollADice(): number {
  const rng = getGlobalSeededRandom();
  return rng.randomInt(1, 7); // 1-6 inclus
}

/**
 * Calcule la valeur d'un dé selon l'expression (ex: "1D+6" -> lance 1 dé + 6)
 * Équivalent de DiceUtil.getValueDice() en Java
 * 
 * @param advValue Expression de dés (ex: "1D+6", "2D", "10", "-5")
 * @returns Valeur calculée
 */
export function getValueDice(advValue: string): number {
  const match = advValue.match(DICE_PATTERN);
  
  if (!match) {
    console.warn(`[DiceUtil] Invalid dice expression: "${advValue}"`);
    return 0;
  }
  
  const sign = match[1];
  const first = match[2];
  const second = match[3];
  
  // Cas simple : entier (10, 5...)
  if (!second) {
    const num = parseInt(first, 10);
    return sign === '-' ? -num : num;
  }
  
  // Cas avec dés : lancer les dés
  const diceCount = parseInt(first, 10);
  let total = 0;
  
  for (let i = 0; i < diceCount; i++) {
    const dice = rollADice();
    total += sign === '-' ? -dice : dice;
  }
  
  // Ajouter le bonus/malus après le "D"
  if (second.length > 1) {
    const bonus = parseInt(second.substring(1), 10);
    total += bonus;
  }
  
  return total;
}

/**
 * Calcule la somme de plusieurs expressions de dés
 * Équivalent de DiceUtil.compute() en Java
 * 
 * @param values Liste d'expressions de dés (ex: ["1D+2", "3D", "5"])
 * @returns Expression combinée (ex: "4D+7")
 */
export function compute(values: string[]): string {
  let constant = 0;
  let dice = 0;
  
  for (const value of values) {
    const match = value.match(DICE_PATTERN);
    
    if (match) {
      const sign = match[1];
      const first = match[2];
      const second = match[3];
      
      // Cas simple : entier
      if (!second) {
        const num = parseInt(first, 10);
        constant += sign === '-' ? -num : num;
      } else {
        // Cas avec dés
        const diceCount = parseInt(first, 10);
        dice += sign === '-' ? -diceCount : diceCount;
        
        if (second.length > 1) {
          const bonus = parseInt(second.substring(1), 10);
          constant += bonus;
        }
      }
    }
  }
  
  // Construire le résultat
  const result: string[] = [];
  if (dice !== 0) {
    result.push(`${dice}D`);
  }
  if (constant !== 0) {
    if (dice !== 0 && constant > 0) {
      result.push('+');
    }
    result.push(constant.toString());
  }
  if (dice === 0 && constant === 0) {
    result.push('0');
  }
  
  return result.join('');
}

export default {
  rollADice,
  getValueDice,
  compute,
};
